import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import {
    NgbDate,
    NgbDateAdapter,
    NgbDateNativeAdapter,
} from '@cmps/ng-bootstrap';
import { IOption } from '@cmps/ng-select';
import { merge, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';

import { CmpsDatePipe } from '../common/date.pipe';
import { PeriodType } from '../widget/date-range.widget.model';

import { TranslationKeyMap } from './insight.i18n';
import { CacheDateItem } from './performance-data/cache-date.pd.model';
import {
    getEventDateValue,
    invalidEventDate,
    PeriodTypeOpts,
} from './period.insight.model';

@Component({
    selector: '[cmpsInsightPeriod]',
    templateUrl: './period.insight.component.html',
    providers: [{ provide: NgbDateAdapter, useClass: NgbDateNativeAdapter }],
})
export class PeriodInsightComponent implements OnChanges, OnInit, OnDestroy {
    @Input() allowedPeriodTypes: PeriodType[];
    @Input() periodType: PeriodType;
    @Output() periodTypeChange: EventEmitter<PeriodType>;
    @Input() cacheDates: CacheDateItem[];
    @Input() eventDate: Date;
    @Output() eventDateChange: EventEmitter<Date>;
    i18n = TranslationKeyMap;
    PeriodType = PeriodType;
    form: FormGroup;
    periodTypeOpts: Array<{ value: PeriodType; tlKey: string }>;
    invalidEventDate: (
        date: NgbDate,
        current: { year: number; month: number },
    ) => boolean;

    eventDateOpts: IOption[];
    private _sub: Subscription;

    constructor(
        private readonly _formBuilder: FormBuilder,
        private readonly _datePipe: CmpsDatePipe,
    ) {
        this.periodTypeChange = new EventEmitter();
        this.eventDateChange = new EventEmitter();
        this.invalidEventDate = () => true;

        this.form = this._formBuilder.group({
            periodType: [null],
            eventDateDay: [null],
            eventDate: [null],
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        // eslint-disable-next-line no-prototype-builtins
        if (changes.hasOwnProperty('allowedPeriodTypes')) {
            this.periodTypeOpts = !this.allowedPeriodTypes
                ? PeriodTypeOpts
                : // eslint-disable-next-line @typescript-eslint/prefer-includes
                  PeriodTypeOpts.filter(
                      (value) =>
                          // eslint-disable-next-line @typescript-eslint/prefer-includes
                          this.allowedPeriodTypes.indexOf(value.value) !== -1,
                  );
        }

        // eslint-disable-next-line no-prototype-builtins
        if (
            // eslint-disable-next-line no-prototype-builtins
            changes.hasOwnProperty('periodType') ||
            // eslint-disable-next-line no-prototype-builtins
            changes.hasOwnProperty('eventDate')
        ) {
            this.form.patchValue(
                {
                    periodType: this.periodType,
                    eventDateDay: this.eventDate,
                    eventDate: [this.eventDate],
                },
                { emitEvent: false },
            );
        }

        // eslint-disable-next-line no-prototype-builtins
        if (changes.hasOwnProperty('cacheDates')) {
            this.invalidEventDate = invalidEventDate(
                this.periodType,
                this.cacheDates,
            );
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            this.eventDateOpts =
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                this.cacheDates &&
                this.cacheDates.map((value) => ({
                    label: getEventDateValue(
                        this.cacheDates,
                        value.eventDate,
                        this.periodType,
                        this._datePipe,
                    ),
                    value: value.eventDate,
                }));
        }
    }

    ngOnInit(): void {
        this._sub = merge(
            this.form.get('periodType').valueChanges.pipe(
                tap((value) => {
                    this.periodTypeChange.emit(value);
                }),
            ),
            merge(
                this.form.get('eventDateDay').valueChanges,
                this.form.get('eventDate').valueChanges,
            ).pipe(
                tap((value: Date) => {
                    value?.setHours(0);
                    this.eventDateChange.emit(value);
                }),
            ),
        ).subscribe();
    }

    ngOnDestroy(): void {
        if (this._sub) {
            this._sub.unsubscribe();
        }
        this.periodTypeChange.complete();
        this.eventDateChange.complete();
    }
}
